import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import http from '@/services/http';
import {
    loadZoneDeliveryStatuses,
    playerIdsByZoneId,
    type CommandHistoryEntry,
    type CommandHistoryPage,
    type PendingChangesPage,
    type PublishStatusRow,
} from '@/utils/syncStatus';

/**
 * Data for the Sync pill and its popover.
 * Every endpoint is an existing tabasco route:
 *
 *  - GET  /api/scheduler/pending-changes          the attributed change feed (cursor paged)
 *  - GET  /api/scheduler/publish-status           per-zone delivery counts
 *  - GET  /api/location_zone_player/{id}/command_history
 *  - POST /api/location_zone_player/search        a zone's players
 */

export type SyncScope = 'mine' | 'everyone';

const FEED_REFETCH_MS = 60_000;
const STATUS_STALE_MS = 30_000;

export function pendingChangesQueryKey(scope: SyncScope) {
    return ['scheduler-pending-changes', scope] as const;
}

/** Query params for one page of the pending-changes feed. */
export function buildPendingChangesParams(scope: SyncScope, beforeId: number | null): Record<string, number> {
    return {
        // Laravel's boolean rule takes 1, not the string "true".
        ...(scope === 'mine' ? { only_mine: 1 } : {}),
        ...(beforeId !== null ? { before_id: beforeId } : {}),
    };
}

/**
 * The pending-changes feed, newest first. "Mine" sends only_mine; "Everyone"
 * sends nothing. Each page is 100 rows; `fetchNextPage` loads the older ones
 * with the before_id cursor the previous page returned.
 */
export function usePendingChangesFeed(scope: MaybeRefOrGetter<SyncScope>) {
    return useInfiniteQuery({
        queryKey: computed(() => pendingChangesQueryKey(toValue(scope))),
        queryFn: ({ pageParam }) =>
            http
                .get('/api/scheduler/pending-changes', {
                    params: buildPendingChangesParams(toValue(scope), pageParam),
                })
                .then((response) => response.data as PendingChangesPage),
        initialPageParam: null as number | null,
        getNextPageParam: (lastPage) => lastPage.next_before_id,
        refetchInterval: FEED_REFETCH_MS,
        retry: 0,
    });
}

function fetchStatusRows(zoneIds: number[]): Promise<PublishStatusRow[]> {
    return http
        .get('/api/scheduler/publish-status', { params: { location_zone_ids: zoneIds } })
        .then((response) => (response.data as { data: PublishStatusRow[] }).data ?? []);
}

/** Delivery status for each zone, keyed by zone id. See loadZoneDeliveryStatuses for masters. */
export function useZoneDeliveryStatuses(zoneIds: MaybeRefOrGetter<number[]>) {
    const sortedZoneIds = computed(() => [...new Set(toValue(zoneIds))].sort((left, right) => left - right));
    return useQuery({
        queryKey: computed(() => ['scheduler-publish-status', sortedZoneIds.value]),
        queryFn: () => loadZoneDeliveryStatuses(sortedZoneIds.value, fetchStatusRows),
        enabled: computed(() => sortedZoneIds.value.length > 0),
        staleTime: STATUS_STALE_MS,
        refetchInterval: FEED_REFETCH_MS,
        retry: 0,
    });
}

/** How many players the popover lists under an expanded zone before it links to the search. */
export const ZONE_PLAYER_PREVIEW_LIMIT = 3;

export interface ZonePlayersPreview {
    players: Record<string, unknown>[];
    total: number;
}

/**
 * The first few players of the given zones, from the Location Zone Player
 * search, which carries each player's last check-in and missing-track count.
 */
export function useZonePlayersPreview(
    zoneIds: MaybeRefOrGetter<number[]>,
    enabled: MaybeRefOrGetter<boolean>
) {
    const sortedZoneIds = computed(() => [...new Set(toValue(zoneIds))].sort((left, right) => left - right));
    return useQuery({
        queryKey: computed(() => ['sync-zone-players', sortedZoneIds.value]),
        queryFn: () =>
            http
                .post('/api/location_zone_player/search', {
                    perPage: ZONE_PLAYER_PREVIEW_LIMIT,
                    withTotal: true,
                    sort: { id: 'asc' },
                    filter: {
                        ruleScopes: {
                            id: 'root',
                            scopeType: 'and',
                            rules: [
                                {
                                    id: 'sync-zone-players',
                                    column: 'location_zone_id',
                                    criterion: 'contains_any',
                                    value: sortedZoneIds.value,
                                    field: null,
                                },
                            ],
                        },
                    },
                })
                .then((response) => {
                    const payload = response.data as { data?: Record<string, unknown>[]; meta?: { total?: number | null } };
                    const players = payload.data ?? [];
                    return { players, total: payload.meta?.total ?? players.length } satisfies ZonePlayersPreview;
                }),
        enabled: computed(() => toValue(enabled) && sortedZoneIds.value.length > 0),
        staleTime: STATUS_STALE_MS,
        retry: 0,
    });
}

/**
 * The players of many zones in one Location Zone Player search, as ids by
 * zone. The popover asks this for every zone on the page with one or two
 * players, so those rows can show the ids themselves.
 */
export function useRowPlayerIds(zoneIds: MaybeRefOrGetter<number[]>, playerCount: MaybeRefOrGetter<number>) {
    const sortedZoneIds = computed(() => [...new Set(toValue(zoneIds))].sort((left, right) => left - right));
    return useQuery({
        queryKey: computed(() => ['sync-row-players', sortedZoneIds.value]),
        queryFn: () =>
            http
                .post('/api/location_zone_player/search', {
                    // The status counts the players, so this is one page holding all of them.
                    perPage: Math.min(Math.max(toValue(playerCount), 1), 200),
                    withTotal: false,
                    sort: { id: 'asc' },
                    filter: {
                        ruleScopes: {
                            id: 'root',
                            scopeType: 'and',
                            rules: [
                                {
                                    id: 'sync-row-players',
                                    column: 'location_zone_id',
                                    criterion: 'contains_any',
                                    value: sortedZoneIds.value,
                                    field: null,
                                },
                            ],
                        },
                    },
                })
                .then((response) =>
                    playerIdsByZoneId((response.data as { data?: Record<string, unknown>[] }).data ?? [])
                ),
        enabled: computed(() => sortedZoneIds.value.length > 0),
        staleTime: STATUS_STALE_MS,
        retry: 0,
    });
}

/**
 * Enough command history to find the newest content command. Most commands
 * between two deliveries are content commands or a handful of others, so one
 * page of 25 is plenty.
 */
const COMMAND_HISTORY_LIMIT = 25;

/**
 * The first element of every command history cache key. Invalidating it after
 * a command is issued or sent reaches the Sync popover and the inspector tab.
 */
export const COMMAND_HISTORY_QUERY_ROOT = 'location-zone-player-command-history';

/** A player's command history, newest first. Fetched only while `enabled` is true. */
export function usePlayerCommandHistory(
    locationZonePlayerId: MaybeRefOrGetter<number | null>,
    enabled: MaybeRefOrGetter<boolean> = true
) {
    return useQuery({
        queryKey: computed(() => [COMMAND_HISTORY_QUERY_ROOT, toValue(locationZonePlayerId)]),
        queryFn: () =>
            http
                .get(`/api/location_zone_player/${toValue(locationZonePlayerId)}/command_history`, {
                    params: { limit: COMMAND_HISTORY_LIMIT },
                })
                .then((response) => (response.data as { data: CommandHistoryEntry[] }).data ?? []),
        enabled: computed(() => toValue(enabled) && toValue(locationZonePlayerId) !== null),
        staleTime: STATUS_STALE_MS,
        refetchInterval: 30_000,
        retry: 0,
    });
}

/** Rows per page of the player inspector's Command History tab. */
export const COMMAND_HISTORY_PAGE_SIZE = 50;

/**
 * The paged history's cache key. It starts with the Sync popover's key, so
 * invalidating COMMAND_HISTORY_QUERY_ROOT reaches both, but its third element
 * keeps the two in separate cache entries.
 */
export function playerCommandHistoryPagesQueryKey(locationZonePlayerId: number | null) {
    return [COMMAND_HISTORY_QUERY_ROOT, locationZonePlayerId, 'pages'] as const;
}

/**
 * A player's whole command history, newest first, one page of 50 at a time.
 * `fetchNextPage` loads older rows with the cursor the previous page returned.
 * It never polls: the tab that uses it reloads when it is shown and stale.
 */
export function usePlayerCommandHistoryPages(locationZonePlayerId: MaybeRefOrGetter<number | null>) {
    return useInfiniteQuery({
        queryKey: computed(() => playerCommandHistoryPagesQueryKey(toValue(locationZonePlayerId))),
        queryFn: ({ pageParam, signal }) =>
            http
                .get(`/api/location_zone_player/${toValue(locationZonePlayerId)}/command_history`, {
                    params: {
                        limit: COMMAND_HISTORY_PAGE_SIZE,
                        ...(pageParam !== null ? { cursor: pageParam } : {}),
                    },
                    signal,
                })
                .then((response) => response.data as CommandHistoryPage),
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
        enabled: computed(() => toValue(locationZonePlayerId) !== null),
        staleTime: STATUS_STALE_MS,
        retry: 0,
    });
}
